/** Recharge plan routing for the current offer and optional free-vape experiment. */
(function () {
  'use strict';
  if (window.csSubscriptionDiscount) return;

  const MARKER = 'with 5% discount';
  const SELECT = '.subscription__select[data-cs-discount-select]';
  const optionsBySelect = new WeakMap();
  let treatment = false;
  let vipTreatment = false;
  let refreshing = false;
  let refreshTimer = null;

  const nameOf = (option) => option?.getAttribute('data-name') || option?.getAttribute('data-selling-plan-name') || '';
  const frequencyOf = (name) => String(name || '').toLowerCase().replace(MARKER, '').replace(/\s+/g, ' ').trim();
  const isDiscounted = (option) => String(nameOf(option)).toLowerCase().includes(MARKER);
  const selectedOption = () => document.querySelector(SELECT)?.selectedOptions[0] || null;
  const isSubscribed = () => Array.from(document.querySelectorAll(
    '.subscription__checkbox[data-sync-group="subscription"][data-type="subscribe"]'
  )).some((checkbox) => checkbox.checked);

  function readOptions(select) {
    if (!optionsBySelect.has(select)) {
      const template = select.parentElement.querySelector('template[data-cs-discount-plans]');
      optionsBySelect.set(select, [
        ...Array.from(select.options),
        ...Array.from(template?.content.querySelectorAll('option') || [])
      ].map((option) => option.cloneNode(true)));
    }
    return optionsBySelect.get(select);
  }

  const usesDiscount = (handle) => window.csSubscriptionIncentive?.usesDiscount?.(handle) ?? treatment;

  function allowedOptions(options) {
    const regular = options.filter((option) => !isDiscounted(option));
    if (!treatment) return regular;
    // Keep the same cadence at its regular price if its new plan is inactive.
    return regular.map((option) => options.find((candidate) =>
      isDiscounted(candidate) && frequencyOf(nameOf(candidate)) === frequencyOf(nameOf(option))
    ) || option);
  }

  function planForVariant(variantId, planId, planName) {
    const id = String(variantId || '').trim();
    const card = Array.from(document.querySelectorAll('.product-variant-card[data-variant-id]'))
      .find((element) => element.dataset.variantId === id);
    if (!card) return null;
    const plans = Array.from(card.querySelectorAll('.product-subscription-list [data-selling-plan-id]'));
    const name = planName || nameOf(selectedOption());
    const frequency = frequencyOf(name);
    const candidates = plans.filter((plan) => frequencyOf(nameOf(plan)) === frequency);
    return (usesDiscount(card.dataset.productHandle || undefined) && candidates.find(isDiscounted)) || candidates.find((plan) => !isDiscounted(plan)) || null;
  }

  function resolvePlanId(variantId, planId, planName) {
    if (!planId) return '';
    const plan = planForVariant(variantId, planId, planName);
    if (plan) return plan.getAttribute('data-selling-plan-id');
    // Never send the main product's ID to a VIP variant with its own plans.
    const card = document.querySelector(`.product-variant-card[data-variant-id="${Number(variantId)}"]`);
    return card ? '' : String(planId);
  }

  function variantPrice(variantId, basePrice, planId, planName) {
    if (!isSubscribed()) return Number(basePrice) || 0;
    const option = selectedOption();
    const plan = planForVariant(variantId, planId || option?.value, planName || nameOf(option));
    const price = plan?.getAttribute('data-cs-plan-price');
    return price != null && Number.isFinite(Number(price)) ? Number(price) : Number(basePrice) || 0;
  }

  function canSwitchToDiscountedSubscription() {
    const benefit = (treatment && isDiscounted(selectedOption())) ||
      (window.csSubscriptionIncentive?.isVape() && selectedOption());
    return benefit && !isSubscribed() && Boolean(
      document.querySelector('.subscription__checkbox[data-sync-group="subscription"][data-type="buy-once"]:checked') &&
      document.querySelector('.subscription__checkbox[data-sync-group="subscription"][data-type="subscribe"]:not(:disabled)')
    );
  }

  function updateSwitchCopy(button, handle) {
    const vape = Boolean(window.csSubscriptionIncentive?.isVape(handle));
    const discountCopy = button.querySelector('[data-cs-discount-copy]');
    const vapeCopy = button.querySelector('[data-cs-vape-copy]');
    if (discountCopy) discountCopy.hidden = vape;
    if (vapeCopy) vapeCopy.hidden = !vape;
  }

  function refreshPrices() {
    document.querySelectorAll('[data-cs-subscription-switch]').forEach((button) => {
      button.hidden = !canSwitchToDiscountedSubscription();
      updateSwitchCopy(button);
    });
    document.querySelectorAll('.product-variant-card[data-variant-id]').forEach((card) => {
      const priceElement = card.querySelector('.product-variant-card__price');
      // Liquid-rendered money strings keep the customer's currency formatting.
      if (priceElement) {
        if (!priceElement.dataset.csOriginalPrice) priceElement.dataset.csOriginalPrice = priceElement.textContent;
        const plan = isSubscribed() ? planForVariant(card.dataset.variantId) : null;
        const formatted = plan?.getAttribute('data-cs-plan-money');
        priceElement.textContent = formatted || priceElement.dataset.csOriginalPrice;
      }
    });
    const option = selectedOption();
    const hasDiscount = treatment && isDiscounted(option);
    document.querySelectorAll('[data-cs-subscription-discount-badge]').forEach((badge) => { badge.hidden = !hasDiscount; });
    const hasVape = Boolean(window.csSubscriptionIncentive?.isVape() && option);
    document.querySelectorAll('[data-cs-subscription-vape-badge]').forEach((badge) => { badge.hidden = !hasVape; });
    document.querySelectorAll('[data-cs-subscription-control-badge]').forEach((badge) => { badge.hidden = hasDiscount || hasVape; });
    const manager = window.productSelectionManager;
    if (manager) {
      manager.updatePricingDisplay();
      manager.updateSavingsBadge();
      manager.applyOptimisticCartDrawerFromSelection();
    }
  }

  function refresh(preferredFrequency) {
    if (refreshing) return;
    refreshing = true;
    const changed = [];
    const sharedFrequency = preferredFrequency || frequencyOf(nameOf(selectedOption()));
    document.querySelectorAll(SELECT).forEach((select) => {
      const previousValue = select.value;
      const frequency = sharedFrequency || frequencyOf(nameOf(select.selectedOptions[0]));
      const options = allowedOptions(readOptions(select));
      const ids = options.map((option) => option.value).join(',');
      if (Array.from(select.options).map((option) => option.value).join(',') !== ids) {
        select.replaceChildren(...options.map((option) => option.cloneNode(true)));
      }
      const match = Array.from(select.options).find((option) => frequencyOf(nameOf(option)) === frequency);
      select.value = match?.value || select.options[0]?.value || '';
      if (previousValue !== select.value) changed.push(select);
    });
    // Notify the existing input synchronizer and cart managers after ALL selects agree.
    changed.forEach((select) => select.dispatchEvent(new Event('change', { bubbles: true })));
    refreshing = false;
    refreshPrices();
    if (changed.length) document.dispatchEvent(new CustomEvent('cs:subscription-plans-changed'));
  }

  function decide() {
    const wasVipTreatment = vipTreatment;
    vipTreatment = true;
    treatment = window.csSubscriptionIncentive?.usesDiscount?.() ??
      (document.body.dataset.csSubscriptionDiscountEnabled !== 'false' && !window.csSubscriptionIncentive?.isVape());
    document.body.dataset.csSubscriptionDiscount = treatment ? 'on' : 'off';
    refresh();
    if (wasVipTreatment !== vipTreatment) document.dispatchEvent(new CustomEvent('cs:vip-treatment-changed'));
    // Refresh product-specific offers even when the page offer stays the same.
    document.dispatchEvent(new CustomEvent('cs:subscription-treatment-changed'));
  }

  function eligiblePlans(plans, handle) {
    const regular = plans.filter((plan) => !String(plan.name || '').toLowerCase().includes(MARKER));
    if (!usesDiscount(handle)) return regular;
    return regular.map((plan) => plans.find((candidate) =>
      String(candidate.name || '').toLowerCase().includes(MARKER) &&
      frequencyOf(candidate.name) === frequencyOf(plan.name)
    ) || plan);
  }

  window.csSubscriptionDiscount = {
    resolvePlanId, variantPrice, refresh, refreshPrices, updateSwitchCopy,
    eligiblePlans, planFrequency: frequencyOf, isTreatment: usesDiscount,
    isVipTreatment: () => vipTreatment
  };

  function init() {
    decide();
    window.addEventListener('pageshow', decide);
    document.addEventListener('cs:subscription-incentive-changed', decide);
    document.addEventListener('change', (event) => {
      if (event.target.matches(SELECT)) refresh(frequencyOf(nameOf(event.target.selectedOptions[0])));
      if (event.target.matches('.subscription__checkbox')) window.setTimeout(refreshPrices, 0);
    }, true);
    document.addEventListener('click', (event) => {
      const button = event.target.closest('[data-cs-subscription-switch]');
      if (!button || !canSwitchToDiscountedSubscription()) return;
      const checkbox = document.querySelector(
        '.subscription__checkbox[data-sync-group="subscription"][data-type="subscribe"]:not(:disabled)'
      );
      // Use the existing mode synchronizer, pricing and cart listeners.
      checkbox.checked = true;
      checkbox.dispatchEvent(new Event('change', { bubbles: true }));
      refreshPrices();
      button.closest('.modal-add-to-cart')?.querySelector('.custom-add-to-cart-btn')?.focus({ preventScroll: true });
    });
    document.addEventListener('cart:updated', refreshPrices);
    new MutationObserver((mutations) => {
      if (!mutations.some((mutation) => Array.from(mutation.addedNodes).some((node) =>
        node.nodeType === 1 && (node.matches(SELECT + ', .product-variant-card, [data-cs-subscription-switch]') || node.querySelector(SELECT + ', .product-variant-card, [data-cs-subscription-switch]'))
      ))) return;
      window.clearTimeout(refreshTimer);
      refreshTimer = window.setTimeout(() => refresh(), 0);
    }).observe(document.body, { childList: true, subtree: true });
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
